import type { RepositoryResult } from "../contracts";

export type CursorScalar = string | number | boolean | null;

export interface KeysetCursor {
  version: 1;
  order: string;
  values: CursorScalar[];
}

const cursorFailure = (message: string): RepositoryResult<KeysetCursor> => ({
  ok: false,
  error: { code: "validation_failure", message, retryable: false },
});

const toBase64Url = (value: string) => {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/u, "");
};

const fromBase64Url = (value: string) => {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(padded);
  return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)));
};

const isScalar = (value: unknown): value is CursorScalar =>
  value === null || ["string", "number", "boolean"].includes(typeof value);

export function encodeCursor(cursor: KeysetCursor): string {
  if (cursor.version !== 1 || !cursor.order.trim() || !cursor.values.every(isScalar)) {
    throw new Error("Cannot encode an invalid keyset cursor.");
  }
  return toBase64Url(JSON.stringify(cursor));
}

export function decodeCursor(
  encoded: string,
  expectedOrder: string,
  expectedValueCount: number,
): RepositoryResult<KeysetCursor> {
  if (!encoded || encoded.length > 2048 || !/^[A-Za-z0-9_-]+$/u.test(encoded)) {
    return cursorFailure("The pagination cursor is malformed.");
  }
  try {
    const parsed = JSON.parse(fromBase64Url(encoded)) as Partial<KeysetCursor>;
    if (
      parsed.version !== 1 ||
      parsed.order !== expectedOrder ||
      !Array.isArray(parsed.values) ||
      parsed.values.length !== expectedValueCount ||
      !parsed.values.every(isScalar)
    ) {
      return cursorFailure("The pagination cursor does not match this query.");
    }
    return { ok: true, value: parsed as KeysetCursor };
  } catch {
    return cursorFailure("The pagination cursor could not be decoded.");
  }
}

